import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import InputDropdown, {
  mergeReactSelectStyles,
  buildInputDropdownStyles,
} from "../InputFields/InputDropdown";
import { formatUSD } from "../../utils/usFormat";
import "./MaterialsLaborSelect.css";

/** Same outer height as line-item fields (materials row) — match Price/Total box */
const LINE_CONTROL_H = "60px";

const MSL_INPUT_TYPO = {
  fontSize: 15,
  lineHeight: "1.4",
  fontFamily: "inherit",
};

/**
 * Rich option row for materials / labor (react-select Option component).
 */
export function MaterialsLaborOption(props) {
  const { innerRef, innerProps, data, isFocused, isSelected } = props;
  const isNonTaxable =
    data?.IsNonTaxable === true ||
    data?.IsNonTaxable === "true" ||
    data?.IsNonTaxable === 1;
  const taxableText = isNonTaxable ? "Non-Taxable" : "Taxable";

  const cost =
    data?.CostPerUnit ??
    data?.CostPerHour ??
    data?.CostPerSquare ??
    data?.CostPerFixed;
  const priceLabel =
    cost !== undefined && cost !== null && cost !== ""
      ? formatUSD(cost)
      : "";

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        "msl-option",
        isFocused && "msl-option--focused",
        isSelected && "msl-option--selected",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="msl-option__title-row">
        <span className="msl-option__name">{data?.Name}</span>
        <span
          className={
            isNonTaxable ? "msl-option__tax msl-option__tax--nontax" : "msl-option__tax"
          }
        >
          ({taxableText})
        </span>
      </div>
      <div className="msl-option__detail-row">
        <span className="msl-option__desc">{data?.Description}</span>
        {priceLabel ? (
          <span className="msl-option__price">{priceLabel}</span>
        ) : null}
      </div>
    </div>
  );
}

function materialsLaborStyleExtras(hasError, isDark = false) {
  const err = Boolean(hasError);
  const ink = isDark ? "#e8eaed" : "#374151";
  const muted = isDark ? "#9aa0a6" : "#64748b";
  const menuBg = isDark ? "#1a2332" : "#ffffff";
  const menuBorder = isDark ? "#2d3b4e" : "#e5e7eb";
  return {
    group: (base) => ({
      ...base,
      paddingTop: 4,
      paddingBottom: 2,
    }),
    groupHeading: (base) => ({
      ...base,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: "#94a3b8",
      textTransform: "uppercase",
      padding: "8px 10px 4px",
      margin: 0,
    }),
    option: (base, state) => ({
      ...base,
      padding: 0,
      backgroundColor: "transparent",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      fontSize: 15,
      lineHeight: 1.45,
      color: err ? "#dc3545" : ink,
      fontWeight: state.isSelected ? 600 : 400,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 13000,
      borderRadius: 8,
      overflow: "hidden",
      marginTop: 6,
      marginBottom: 4,
      boxShadow: "var(--shadow-xl)",
      border: `1px solid ${menuBorder}`,
      backgroundColor: menuBg,
      color: ink,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 280,
      padding: "6px 4px",
      overflowY: "auto",
      overflowX: "hidden",
      backgroundColor: menuBg,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 13000,
    }),
    noOptionsMessage: (base) => ({
      ...base,
      padding: 0,
      color: muted,
    }),
    placeholder: (base) => ({
      ...base,
      color: err ? "#dc3545" : isDark ? "#9aa0a6" : "#9ca3af",
      ...MSL_INPUT_TYPO,
      margin: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: err ? "#dc3545" : ink,
      ...MSL_INPUT_TYPO,
      margin: 0,
    }),
    input: (base) => ({
      ...base,
      color: ink,
      ...MSL_INPUT_TYPO,
    }),
  };
}

/**
 * Line-item materials & labor field — built on shared {@link InputDropdown} (§7 chrome + custom options).
 */
export default function MaterialsLaborSelect({
  hasError = false,
  styles: userStyles,
  className,
  classNamePrefix = "mslabor",
  components,
  menuPortalTarget,
  options,
  value,
  onChange,
  onInputChange,
  inputValue,
  placeholder,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  ...rest
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const styles = useMemo(
    () =>
      mergeReactSelectStyles(
        mergeReactSelectStyles(
          buildInputDropdownStyles(LINE_CONTROL_H, hasError, isDarkMode),
          materialsLaborStyleExtras(hasError, isDarkMode)
        ),
        userStyles
      ),
    [hasError, userStyles, isDarkMode]
  );

  const portalTarget =
    menuPortalTarget !== undefined
      ? menuPortalTarget
      : typeof document !== "undefined"
        ? document.body
        : undefined;

  return (
    <InputDropdown
      label={undefined}
      options={options}
      value={value}
      onChange={(_, selected) => {
        onChange?.(selected);
      }}
      onTextFieldChange={(ev, val) => {
        onInputChange?.(val, { action: "input-change" });
      }}
      inputValue={inputValue}
      placeholder={placeholder}
      error={hasError ? true : undefined}
      fieldHeight={LINE_CONTROL_H}
      getOptionLabel={(o) =>
        o?.Name ?? o?.label ?? o?.name ?? String(o ?? "")
      }
      styles={styles}
      components={components}
      className={["msl-select-root", className].filter(Boolean).join(" ")}
      selectProps={{
        menuIsOpen,
        onMenuOpen,
        onMenuClose,
        menuPortalTarget: portalTarget,
        classNamePrefix,
        ...rest,
      }}
    />
  );
}
